// Типизированный клиент кассы. Каждый ответ прогоняется через zod-схему
// из @dastarhan/contracts: если сервер прислал не то — узнаём на границе,
// а не глубоко в рендере.
import type { ZodType } from 'zod';
import { get, set } from 'idb-keyval';
import { z } from 'zod';
import { CurrentShift, ShiftBrief, ShiftReport,
  ActivateResponse, CatalogResponse, ElevateResponse, GuestLookupResponse, HallMap, OpenOrdersList, OrderDetail, PosClockOutResponse, PosSession, ReservationsResponse, ShiftOrders, ShiftSummary, SyncResponse,
  type ActivateRequest, type ElevateRequest, type GuestLookupRequest, type PosClockOutRequest, type PosLoginRequest, type SyncRequest, type UpsertOpenOrder,
} from '@dastarhan/contracts';

/**
 * Адрес сервера.
 *
 * В браузере он относительный: касса открыта с того же домена,
 * что и API, и лишний хост только мешал бы.
 *
 * В программе для Windows файлы лежат на диске, и относительный
 * адрес вёл бы в саму папку с программой. Поэтому оболочка передаёт
 * настоящий адрес в ярлыке — `?api=https://...`, и мы его запоминаем:
 * при переходах внутри кассы строка запроса теряется, а сервер нужен
 * до самого закрытия смены.
 */
function resolveBase(): string {
  if (typeof window === 'undefined') return '/api/v1';

  const fromUrl = new URLSearchParams(window.location.search).get('api');
  if (fromUrl) {
    try { localStorage.setItem('dastarhan.api', fromUrl); } catch { /* приват-режим */ }
    return `${fromUrl.replace(/\/$/, '')}/api/v1`;
  }

  // Локальный файл: без сохранённого адреса касса не найдёт сервер
  if (window.location.protocol === 'file:') {
    const saved = (() => {
      try { return localStorage.getItem('dastarhan.api'); } catch { return null; }
    })();
    if (saved) return `${saved.replace(/\/$/, '')}/api/v1`;
  }

  return '/api/v1';
}

export const BASE = resolveBase();

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string) {
    super(`${status} ${code}`);
  }
}

async function call<T>(
  path: string,
  schema: ZodType<T>,
  init: { method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'; body?: unknown; token?: string } = {},
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: init.method ?? 'GET',
    headers: {
      ...(init.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(init.token ? { Authorization: `Bearer ${init.token}` } : {}),
    },
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  if (!res.ok) {
    const data = (await res.json().catch(() => null)) as { code?: string } | null;
    // Устройство отвязали в офисе: планшет сломался, украден или продан.
    // Стираем привязку здесь же — иначе касса будет молча биться
    // в закрытую дверь до конца смены и путать кассира
    if (res.status === 401 && data?.code === 'TERMINAL_REVOKED') {
      localStorage.removeItem('dastarhan.deviceKey');
      localStorage.removeItem('dastarhan.token');
    }
    throw new ApiError(res.status, data?.code ?? 'HTTP_ERROR');
  }
  return schema.parse(await res.json());
}

export const api = {
  activate: (body: ActivateRequest) =>
    call('/terminals/activate', ActivateResponse, { method: 'POST', body }),
  posLogin: (body: PosLoginRequest) =>
    call('/auth/pos', PosSession, { method: 'POST', body }),
  posClockOut: (body: PosClockOutRequest) =>
    call('/auth/pos-out', PosClockOutResponse, { method: 'POST', body }),
  /**
   * Меню с запасом на диске.
   *
   * Касса включается утром, а сеть у заведения может лежать со вчера.
   * Без запаса кассир увидел бы пустой экран и не смог бы принять
   * ни одного заказа — при том, что кухня работает и гости пришли.
   *
   * Свежее меню сохраняем при каждой удачной загрузке; при обрыве
   * достаём последнее известное. Оно может отстать на день по ценам —
   * это несравнимо меньшая беда, чем закрытая касса.
   */
  catalog: async (token: string) => {
    try {
      const fresh = await call('/menu/catalog', CatalogResponse, { token });
      void set('catalog.cache', { at: Date.now(), data: fresh });
      return fresh;
    } catch (e) {
      const kept = await get<{ at: number; data: unknown }>('catalog.cache');
      if (!kept) throw e;
      return CatalogResponse.parse(kept.data);
    }
  },

  /** Когда меню в последний раз приходило с сервера. */
  catalogAge: async (): Promise<number | null> => {
    const kept = await get<{ at: number }>('catalog.cache');
    return kept ? Date.now() - kept.at : null;
  },
  sync: (token: string, body: SyncRequest) =>
    call('/sync/events', SyncResponse, { method: 'POST', body, token }),
  shiftSummary: (token: string, shiftId: string) =>
    call(`/shifts/${shiftId}/summary`, ShiftSummary, { token }),
  /** Полный отчёт смены: кормит X, Z и Акт реализации. */
  shiftReport: (token: string, shiftId: string) =>
    call(`/shifts/${shiftId}/report`, ShiftReport, { token }),
  /** Последние смены точки — «Прошлые смены» на кассе. */
  shiftsRecent: (token: string) =>
    call('/shifts/recent', ShiftBrief.array(), { token }),
  /** Текущая смена терминала — правда сервера против памяти кассы. */
  shiftCurrent: (token: string) =>
    call('/shifts/current', CurrentShift, { token }),
  /** Отчёт за операционный день: граница — настройка заведения (06:00). */
  dayReport: (token: string, dateISO: string) =>
    call(`/shifts/day/${dateISO}`, ShiftReport, { token }),
  shiftOrders: (token: string, shiftId: string) =>
    call(`/shifts/${shiftId}/orders`, ShiftOrders, { token }),
  orderDetail: (token: string, orderId: string) =>
    call(`/shifts/orders/${orderId}`, OrderDetail, { token }),
  hallMap: (token: string) =>
    call('/hall/map', HallMap, { token }),
  reservationsToday: (token: string) =>
    call('/reservations', ReservationsResponse, { token }),
  /** Брони на конкретную дату — кассиру, чтобы подготовить зал на утро. */
  reservationsOn: (token: string, date: string) =>
    call(`/reservations?date=${date}`, ReservationsResponse, { token }),
  openOrders: (token: string) =>
    call('/open-orders', OpenOrdersList, { token }),
  openOrderPut: (token: string, body: UpsertOpenOrder) =>
    call('/open-orders', z.object({ ok: z.literal(true) }), { method: 'PUT', body, token }),
  openOrderTake: (token: string, orderId: string) =>
    call(`/open-orders/${orderId}`, z.object({ ok: z.literal(true) }), { method: 'DELETE', token }),

  /** Перенос заказа целиком: гость пересел или столы объединились. */
  transferOrder: (token: string, body: { orderId: string; toTableId: string }) =>
    call('/open-orders/transfer', z.object({ ok: z.literal(true), merged: z.boolean() }),
      { method: 'POST', body, token }),

  /** Перенос отдельных позиций: одно блюдо ушло за соседний стол. */
  transferItems: (token: string, body: { orderId: string; toTableId: string; itemIds: string[] }) =>
    call('/open-orders/transfer-items', z.object({ ok: z.literal(true), moved: z.number() }),
      { method: 'POST', body, token }),
  redeemCode: (token: string, code: string) =>
    call('/guests/redeem-code', z.object({ percent: z.number().int() }),
      { method: 'POST', body: { code }, token }),
  guestPatch: (token: string, guestId: string, body: { note: string | null }) =>
    call(`/guests/${guestId}`, z.object({ ok: z.literal(true) }), { method: 'PATCH', body, token }),
  guestLookup: (token: string, body: GuestLookupRequest) =>
    call('/guests/lookup', GuestLookupResponse, { method: 'POST', body, token }),
  elevate: (token: string, body: ElevateRequest) =>
    call('/auth/elevate', ElevateResponse, { method: 'POST', body, token }),
};
